import { cn } from "@/lib/utils";

/**
 * Hairline KPI strip — N equal columns separated by vertical rules, with a
 * top and bottom rule instead of cards. Values crossfade when they change.
 */
export interface KpiItem {
  label: string;
  value: string;
  sub?: string | null;
  subTone?: "muted" | "success" | "danger" | "warning";
  title?: string;
}

const TONE: Record<NonNullable<KpiItem["subTone"]>, string> = {
  muted: "text-text-muted",
  success: "text-[#10a37f]",
  danger: "text-[#dc2626] dark:text-[#ef4444]",
  warning: "text-[#d97706] dark:text-[#f59e0b]",
};

export function KpiStrip({
  items,
  loading = false,
  valueSize = "lg",
  className,
}: {
  items: KpiItem[];
  loading?: boolean;
  valueSize?: "lg" | "md";
  className?: string;
}) {
  return (
    <div
      className={cn("grid border-b border-t border-border", className)}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      role="list"
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          role="listitem"
          title={item.title}
          className={cn(
            "min-w-0 py-3 px-[14px]",
            i === 0 && "pl-0",
            i === items.length - 1 && "pr-0",
            i > 0 && "border-l border-border",
          )}
        >
          <div className="truncate text-[11px] text-text-muted">{item.label}</div>
          {loading ? (
            <div className="mt-[7px] h-[22px] w-20 animate-pulse rounded bg-surface-hover" />
          ) : (
            <div
              key={item.value}
              className={cn(
                "mt-[5px] truncate font-semibold tabular-nums tracking-[-0.02em] text-text-primary animate-[kpi-fade_150ms_ease-out]",
                valueSize === "lg" ? "text-[22px] leading-[26px]" : "text-[19px] leading-[23px]",
              )}
            >
              {item.value}
            </div>
          )}
          {loading ? (
            <div className="mt-[6px] h-[11px] w-24 animate-pulse rounded bg-surface-hover" />
          ) : (
            item.sub !== undefined && (
              <div className={cn("mt-[3px] truncate whitespace-nowrap text-[11px]", TONE[item.subTone ?? "muted"])}>
                {item.sub ?? " "}
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
}
